// Comprehensive Creative Analysis Service
// This service provides advanced analysis using LLM for ad name clustering and comprehensive creative insights
// Default date range: Last 7 days (can be overridden by passing dateRange parameter)

import axios from 'axios';

// Detect if we're in production by checking the current URL
const isProduction = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
const API_BASE_URL = isProduction 
  ? '' 
  : (process.env.REACT_APP_API_URL || 'http://localhost:5001');

// Configure axios base URL - use explicit localhost:5001 for development
const apiClient = axios.create({
  baseURL: API_BASE_URL
});

export const comprehensiveCreativeAnalysis = {
  
  // Helper function to generate SQL date range condition (uses PST timezone)
  getDefaultDateRange() {
    return `adp.date >= ((CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '7 days')`;
  },

  // Helper function to get a broader date range if default returns no data
  getBroaderDateRange() {
    return `adp.date >= ((CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '30 days')`;
  },

  // Helper function to build ad name filter clause with proper keyword logic
  buildAdNameFilterClause(adNameFilter) {
    if (!adNameFilter) return '';
    
    // Handle both string (legacy) and object (new) formats
    let keywords = [];
    let logic = 'AND'; // default logic
    let mode = 'include'; // default mode
    
    if (typeof adNameFilter === 'string') {
      // Legacy string format - split by spaces
      keywords = adNameFilter.trim().split(/\s+/).filter(k => k.length > 0);
    } else if (typeof adNameFilter === 'object') {
      // New object format with keywords array, logic, and mode
      if (Array.isArray(adNameFilter.keywords)) {
        keywords = adNameFilter.keywords.filter(k => k && k.trim());
        logic = adNameFilter.logic || 'AND';
        mode = adNameFilter.mode || 'include';
      } else if (Array.isArray(adNameFilter)) {
        // Array of keywords (legacy)
        keywords = adNameFilter.filter(k => k && k.trim());
      }
    }
    
    if (keywords.length === 0) return '';
    
    // Build individual keyword conditions
    const keywordConditions = keywords.map(keyword => 
      `a.name ILIKE '%${keyword.trim()}%'`
    );
    
    // Join with AND or OR logic
    const joinOperator = logic === 'OR' ? ' OR ' : ' AND ';
    const combinedCondition = keywordConditions.join(joinOperator);
    
    // Apply include/exclude mode
    if (mode === 'exclude') {
      return `AND NOT (${combinedCondition})`;
    } else {
      return `AND (${combinedCondition})`;
    }
  },



  // Get raw copy content for LLM clustering analysis
  async getCopyContentForClustering(accountId, dateRange = null, adNameFilter = null) {
    try {
      const dateCondition = dateRange || this.getDefaultDateRange();
      const adNameFilterClause = this.buildAdNameFilterClause(adNameFilter);
      
      
      const fullQuery = `
          SELECT 
            a.raw_ad_id,
            a.name as ad_name,
            a.asset_id,
            -- Asset labeling IDs for prioritization
            il.id as image_labeling_id,
            vl.id as video_labeling_id,
            -- Asset URLs for preview
            COALESCE(il.url, vl.url) as asset_url,
            -- Media format detection
            CASE 
              WHEN il.id IS NOT NULL THEN 'Image'
              WHEN vl.id IS NOT NULL THEN 'Video'
              ELSE 'Unknown'
            END as media_format,
            -- Aggregate creative features to avoid GROUP BY issues
            STRING_AGG(DISTINCT COALESCE(il.f_headline, ''), ' | ') as headline,
            STRING_AGG(DISTINCT COALESCE(il.f_primary_text, ''), ' | ') as primary_text,
            STRING_AGG(DISTINCT COALESCE(il.f_description, ''), ' | ') as description,
            STRING_AGG(DISTINCT COALESCE(vl.cf_audio_text_content, ''), ' | ') as audio_text,
            STRING_AGG(DISTINCT COALESCE(vl.cf_text_overlay_content, ''), ' | ') as text_overlay,
            STRING_AGG(DISTINCT COALESCE(vl.cf_visual_description, ''), ' | ') as visual_description,
            STRING_AGG(DISTINCT COALESCE(vl.clip_audio_text, ''), ' | ') as clip_audio,
            SUM(adp.spend) as total_spend,
            SUM(adp.impressions) as total_impressions,
            SUM(adp.clicks) as total_clicks,
            SUM(adp.purchases) as total_conversions,
            SUM(adp.purchase_value) as total_revenue,
            ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) as ctr,
            ROUND((SUM(adp.purchases)::float / NULLIF(SUM(adp.clicks), 0) * 100)::numeric, 4) as cvr,
            ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) as roas
          FROM t_ad a
          JOIN t_ad_daily_performance adp ON a.raw_ad_id = adp.raw_ad_id
          LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
          LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
          WHERE adp.raw_account_id = '${accountId}'
            AND ${dateCondition}
            ${adNameFilterClause}
          GROUP BY a.raw_ad_id, a.name, a.asset_id, il.id, vl.id, il.url, vl.url
          HAVING SUM(adp.impressions) >= 100
          ORDER BY roas DESC, total_spend DESC
        `;

      
      const response = await apiClient.post('/api/chat/sql', {
        query: fullQuery,
        accountId: accountId
      });
      
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching copy content for clustering:', error);
      return [];
    }
  },

  // Get comprehensive ad type analysis
  async getAdTypeAnalysis(accountId, dateRange = null, adNameFilter = null, correlationMetric = 'roas') {
    try {
      const dateCondition = dateRange || this.getDefaultDateRange();
      const adNameFilterClause = this.buildAdNameFilterClause(adNameFilter);
      
      console.log(`🔍 Starting ad type analysis for account: ${accountId}`);
      console.log(`🔍 Date condition: ${dateCondition}`);
      console.log(`🔍 Ad name filter: ${adNameFilterClause || 'None'}`);
      
      // First, let's check what ad types are available
      const checkResponse = await apiClient.post('/api/chat/sql', {
        query: `
          SELECT 
            'Image Labelings' as source,
            COUNT(*) as total_records,
            COUNT(CASE WHEN f_ad_type IS NOT NULL AND f_ad_type != '' THEN 1 END) as with_ad_type,
            STRING_AGG(DISTINCT f_ad_type, ', ') as available_types
          FROM t_ad_image_labelings 
          WHERE raw_account_id = '${accountId}'
          
          UNION ALL
          
          SELECT 
            'Video Labelings' as source,
            COUNT(*) as total_records,
            COUNT(CASE WHEN video_ad_type IS NOT NULL AND video_ad_type != '' THEN 1 END) as with_ad_type,
            STRING_AGG(DISTINCT video_ad_type, ', ') as available_types
          FROM t_ad_video_labelings 
          WHERE raw_account_id = '${accountId}'
        `,
        accountId: accountId
      });

      console.log(`🔍 Ad type availability check:`, checkResponse.data);

      const response = await apiClient.post('/api/chat/sql', {
        query: `
          SELECT 
            COALESCE(il.f_ad_type, vl.video_ad_type, 'Unknown') as ad_type,
            CASE 
              WHEN il.id IS NOT NULL THEN 'Image' 
              WHEN vl.id IS NOT NULL THEN 'Video' 
              ELSE 'Unknown' 
            END as media_type,
            ARRAY_AGG(DISTINCT a.raw_ad_id) as ad_ids,
            COUNT(DISTINCT a.raw_ad_id) as number_of_ads,
            SUM(adp.impressions) as total_impressions,
            SUM(adp.clicks) as total_clicks,
            SUM(adp.spend) as total_spend,
            SUM(adp.purchases) as total_conversions,
            SUM(adp.purchase_value) as total_revenue,
            ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) as ctr,
            ROUND((SUM(adp.purchases)::float / NULLIF(SUM(adp.clicks), 0) * 100)::numeric, 4) as cvr,
            ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) as roas,
            ROUND((SUM(adp.spend)::float / NULLIF(SUM(adp.impressions), 0) * 1000)::numeric, 2) as cpm,
            ROUND((SUM(adp.spend)::float / NULLIF(SUM(adp.purchases), 0))::numeric, 2) as cpa,
            COUNT(DISTINCT adp.date) as active_days,
            MIN(adp.date) as first_seen,
            MAX(adp.date) as last_seen
          FROM t_ad a
          JOIN t_ad_daily_performance adp ON a.raw_ad_id = adp.raw_ad_id
          LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
          LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
          WHERE adp.raw_account_id = '${accountId}'
            AND ${dateCondition}
            ${adNameFilterClause}
          GROUP BY COALESCE(il.f_ad_type, vl.video_ad_type, 'Unknown'), 
                   CASE 
                     WHEN il.id IS NOT NULL THEN 'Image' 
                     WHEN vl.id IS NOT NULL THEN 'Video' 
                     ELSE 'Unknown' 
                   END
          HAVING SUM(adp.impressions) >= 100
            ${correlationMetric === 'cpa' ? 'AND SUM(adp.purchases) > 0' : ''}
          ORDER BY ${correlationMetric === 'revenue' ? 'total_revenue DESC' :
                     correlationMetric === 'ctr' ? 'ctr DESC' :
                     correlationMetric === 'spend' ? 'total_spend DESC' :
                     correlationMetric === 'cpa' ? 'cpa ASC' :
                     'roas DESC'}
        `,
        accountId: accountId
      });

      console.log(`🔍 Ad type analysis response:`, response.data);

      if (response.data.data && response.data.data.length > 0) {
        console.log(`✅ Ad type analysis: ${response.data.data?.length || 0} ad types found`);
        
        // Validate and clean the data types, filtering out unknown/non-applicable entries
        const cleanedData = response.data.data
          .filter(item => 
            item.ad_type && 
            item.ad_type !== 'Unknown' && 
            item.ad_type !== 'N/A' && 
            item.ad_type !== 'n/a' && 
            !item.ad_type.toLowerCase().includes('unknown') &&
            !item.ad_type.toLowerCase().includes('n/a') &&
            !item.ad_type.toLowerCase().includes('not applicable')
          )
          .map(item => ({
            ...item,
            number_of_ads: parseInt(item.number_of_ads) || 0,
            total_impressions: parseInt(item.total_impressions) || 0,
            total_clicks: parseInt(item.total_clicks) || 0,
            total_spend: parseFloat(item.total_spend) || 0,
            total_conversions: parseInt(item.total_conversions) || 0,
            total_revenue: parseFloat(item.total_revenue) || 0,
            ctr: parseFloat(item.ctr) || 0,
            cvr: parseFloat(item.cvr) || 0,
            roas: parseFloat(item.roas) || 0,
            cpm: parseFloat(item.cpm) || 0,
            cpa: parseFloat(item.cpa) || 0,
            active_days: parseInt(item.active_days) || 0,
            media_type: item.media_type || 'Unknown'
          }));

        // Get benchmark data for comparison using the new dedicated endpoint
        
        console.log(`🔍 Fetching benchmarks for ${cleanedData.length} ad types using new endpoint`);
        const benchmarks = await this.getAdTypeBenchmarks(dateRange, cleanedData);
        
        // Add benchmark comparisons to each ad type (exact matching)
        const dataWithBenchmarks = cleanedData.map(item => {
          const benchmark = benchmarks.find(b => 
            b.ad_type === item.ad_type && b.media_type === item.media_type
          );
          
          console.log(`🔍 Matching benchmark for ${item.ad_type} (${item.media_type}):`, benchmark ? `Found - ${benchmark.avg_roas}x ROAS from ${benchmark.account_count} accounts` : 'Not found');
          
          return {
            ...item,
            benchmark: benchmark || null
          };
        });
        
        console.log(`🔍 Cleaned ad type data with benchmarks:`, dataWithBenchmarks);
        console.log(`🔍 Benchmarks available:`, benchmarks);
        console.log(`🔍 Final data sample:`, dataWithBenchmarks.slice(0, 2));
        return dataWithBenchmarks;
      } else {
        console.log(`⚠️ No ad types found with f_ad_type/video_ad_type, trying fallback analysis...`);
        
        // Fallback: Create ad types based on other creative features
        const fallbackResponse = await apiClient.post('/api/chat/sql', {
          query: `
            SELECT 
              CASE 
                WHEN il.id IS NOT NULL THEN 'Image Ad'
                WHEN vl.id IS NOT NULL THEN 'Video Ad'
                ELSE 'Unknown Format'
              END as ad_type,
              COUNT(DISTINCT a.raw_ad_id) as number_of_ads,
              SUM(adp.impressions) as total_impressions,
              SUM(adp.clicks) as total_clicks,
              SUM(adp.spend) as total_spend,
              SUM(adp.purchases) as total_conversions,
              SUM(adp.purchase_value) as total_revenue,
              ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) as ctr,
              ROUND((SUM(adp.purchases)::float / NULLIF(SUM(adp.clicks), 0) * 100)::numeric, 4) as cvr,
              ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) as roas,
              ROUND((SUM(adp.spend)::float / NULLIF(SUM(adp.impressions), 0) * 1000)::numeric, 2) as cpm,
              ROUND((SUM(adp.spend)::float / NULLIF(SUM(adp.purchases), 0))::numeric, 2) as cpa,
              COUNT(DISTINCT adp.date) as active_days,
              MIN(adp.date) as first_seen,
              MAX(adp.date) as last_seen
            FROM t_ad a
            JOIN t_ad_daily_performance adp ON a.raw_ad_id = adp.raw_ad_id
            LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
            LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
            WHERE adp.raw_account_id = '${accountId}'
              AND ${dateCondition}
              ${adNameFilterClause}
            GROUP BY 
              CASE 
                WHEN il.id IS NOT NULL THEN 'Image Ad'
                WHEN vl.id IS NOT NULL THEN 'Video Ad'
                ELSE 'Unknown Format'
              END
            HAVING SUM(adp.impressions) >= 100
            ORDER BY total_spend DESC
          `,
          accountId: accountId
        });

        console.log(`🔍 Fallback ad type analysis response:`, fallbackResponse.data);
        
        if (fallbackResponse.data.data && fallbackResponse.data.data.length > 0) {
          console.log(`✅ Fallback ad type analysis: ${fallbackResponse.data.data?.length || 0} ad types found`);
          
          // Validate and clean the fallback data types
          const cleanedFallbackData = fallbackResponse.data.data.map(item => ({
            ...item,
            number_of_ads: parseInt(item.number_of_ads) || 0,
            total_impressions: parseInt(item.total_impressions) || 0,
            total_clicks: parseInt(item.total_clicks) || 0,
            total_spend: parseFloat(item.total_spend) || 0,
            total_conversions: parseInt(item.total_conversions) || 0,
            total_revenue: parseFloat(item.total_revenue) || 0,
            ctr: parseFloat(item.ctr) || 0,
            cvr: parseFloat(item.cvr) || 0,
            roas: parseFloat(item.roas) || 0,
            cpm: parseFloat(item.cpm) || 0,
            cpa: parseFloat(item.cpa) || 0,
            active_days: parseInt(item.active_days) || 0
          }));
          
          console.log(`🔍 Cleaned fallback ad type data:`, cleanedFallbackData);
          return cleanedFallbackData;
        } else {
          console.error('❌ Both ad type analysis and fallback failed');
          return [];
        }
      }
    } catch (error) {
      console.error('Error getting ad type analysis:', error);
      return [];
    }
  },

  // Get comprehensive top performers analysis
  async getTopPerformersAnalysis(accountId, dateRange = null, adNameFilter = null, sortBy = 'roas') {
    try {
      const dateCondition = dateRange || this.getDefaultDateRange();
      const adNameFilterClause = this.buildAdNameFilterClause(adNameFilter);
      
      // Determine the ORDER BY clause based on sortBy parameter
      let orderByClause;
      if (sortBy === 'spend') {
        orderByClause = 'ORDER BY total_spend DESC';
      } else {
        // Default to ROAS sorting
        orderByClause = 'ORDER BY roas DESC, total_spend DESC';
      }
      
      // First get the AOV for spend threshold calculation
      const aovResponse = await apiClient.post('/api/chat/sql', {
        query: `
          SELECT ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.purchases), 0))::numeric, 2) as aov
          FROM t_ad_daily_performance adp
          WHERE adp.raw_account_id = '${accountId}'
            AND ${dateCondition}
            AND adp.purchases > 0
        `,
        accountId: accountId
      });
      
      const aov = aovResponse.data.data?.[0]?.aov || 100; // Default to $100 if AOV can't be calculated
      const minSpendThreshold = Math.max(100, 1 * aov); // Minimum of $100 or 1 * AOV
      
      console.log('🏆 TopPerformers query params:', {
        accountId,
        dateCondition: dateCondition,
        adNameFilter,
        aov,
        minSpendThreshold,
        sortBy
      });
      
      console.log('🏆 TopPerformers FULL dateCondition:', dateCondition);
      
      const topPerformersQuery = `
          SELECT 
            a.raw_ad_id,
            a.name as ad_name,
            COALESCE(il.f_ad_type, vl.video_ad_type, 'Unknown') as ad_type,
            CASE 
              WHEN il.id IS NOT NULL THEN 'Image'
              WHEN vl.id IS NOT NULL THEN 'Video'
              ELSE 'Unknown'
            END as media_format,
            SUM(adp.impressions) as total_impressions,
            SUM(adp.impressions) as impressions,
            SUM(adp.clicks) as total_clicks,
            SUM(adp.clicks) as clicks,
            SUM(adp.spend) as total_spend,
            SUM(adp.purchases) as total_conversions,
            SUM(adp.purchases) as conversions,
            SUM(adp.purchase_value) as total_revenue,
            ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) as ctr,
            ROUND((SUM(adp.purchases)::float / NULLIF(SUM(adp.clicks), 0) * 100)::numeric, 4) as cvr,
            ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) as roas,
            ROUND((SUM(adp.spend)::float / NULLIF(SUM(adp.impressions), 0) * 1000)::numeric, 2) as cpm,
            ROUND((SUM(adp.spend)::float / NULLIF(SUM(adp.purchases), 0))::numeric, 2) as cpa,
            ROUND((AVG(adp.thumbstop) * 100)::numeric, 2) as hook_rate,
            ROUND((AVG(adp.hold_rate) * 100)::numeric, 2) as hold_rate,
            COUNT(DISTINCT adp.date) as active_days,
            MIN(adp.date) as first_seen,
            MAX(adp.date) as last_seen,
            -- Creative features summary
            COALESCE(il.f_product_position, vl.cf_product_position) as product_position,
            COALESCE(il.f_setting, vl.cf_setting) as setting,
            COALESCE(il.f_lighting, vl.cf_lighting) as lighting,
            COALESCE(il.f_dominant_color, vl.cf_dominant_color) as dominant_color,
            vl.cf_voice_tone as voice_tone,
            vl.cf_music_mood as music_mood,
            COALESCE(il.f_text_alignment, vl.cf_text_alignment) as text_alignment,
            COALESCE(il.f_text_position, vl.cf_text_position) as text_position,
            COALESCE(il.f_text_size, vl.cf_text_size) as text_size,
            COALESCE(il.f_call_to_action_type, vl.cf_call_to_action_type) as cta_type,
            COALESCE(il.f_value_proposition, vl.video_value_proposition) as value_proposition,
            COALESCE(il.f_content_category, vl.cf_content_category) as content_category,
            -- Asset URLs for preview
            COALESCE(il.url, vl.url) as asset_url,
            COALESCE(il.f_description, vl.cf_visual_description) as description
          FROM t_ad a
          JOIN t_ad_daily_performance adp ON a.raw_ad_id = adp.raw_ad_id
          LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
          LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
          WHERE adp.raw_account_id = '${accountId}'
            AND ${dateCondition}
            ${adNameFilterClause}
          GROUP BY a.raw_ad_id, a.name, il.id, vl.id, 
                   il.f_product_position, vl.cf_product_position, il.f_setting, vl.cf_setting,
                   il.f_lighting, vl.cf_lighting, il.f_dominant_color, vl.cf_dominant_color,
                   vl.cf_voice_tone, vl.cf_music_mood, il.f_text_alignment, vl.cf_text_alignment,
                   il.f_text_position, vl.cf_text_position, il.f_text_size, vl.cf_text_size,
                   il.f_call_to_action_type, vl.cf_call_to_action_type, il.f_value_proposition,
                   vl.video_value_proposition, il.f_content_category, vl.cf_content_category,
                   il.url, vl.url, il.f_description, vl.cf_visual_description
          HAVING SUM(adp.impressions) >= 100 
            AND SUM(adp.spend) >= ${minSpendThreshold}
          ${orderByClause}
          LIMIT 50
        `;
      
      console.log('🏆 TopPerformers FULL SQL Query:', topPerformersQuery.substring(0, 1000));
      
      const response = await apiClient.post('/api/chat/sql', {
        query: topPerformersQuery,
        accountId: accountId
      });
      
      const results = response.data.data || [];
      
      // Find the target ad
      const targetAd = results.find(ad => 
        ad.ad_name && ad.ad_name.includes('MissCereal-Replo_Talkover_081425')
      );
      
      console.log('🏆 TopPerformers SQL results:', {
        accountId,
        sortBy,
        resultCount: results.length,
        sampleResults: results.slice(0, 3),
        firstAdSpend: results[0]?.total_spend,
        firstAdRevenue: results[0]?.total_revenue,
        firstAdROAS: results[0]?.roas,
        firstAdImpressions: results[0]?.total_impressions
      });
      
      if (targetAd) {
        console.log('\n🎯 ========== TARGET AD TOP PERFORMERS QUERY ==========');
        console.log('🎯 Ad ID:', targetAd.raw_ad_id);
        console.log('🎯 Ad Name:', targetAd.ad_name);
        console.log('🎯 Date Condition:', dateCondition);
        console.log('🎯 Account ID:', accountId);
        console.log('\n🎯 TOP PERFORMERS SQL QUERY:');
        console.log(topPerformersQuery);
        console.log('\n🎯 TOP PERFORMERS Query Parameters:', {
          accountId,
          dateCondition,
          adNameFilterClause,
          minSpendThreshold,
          sortBy
        });
        console.log('🎯 TOP PERFORMERS Results:', {
          total_spend: targetAd.total_spend,
          total_revenue: targetAd.total_revenue,
          roas: targetAd.roas,
          ctr: targetAd.ctr,
          total_impressions: targetAd.total_impressions,
          total_clicks: targetAd.total_clicks
        });
        console.log('🎯 ============================================\n');
      }
      
      return results;
    } catch (error) {
      console.error('Error fetching top performers analysis:', error);
      return [];
    }
  },

  // Get landing page performance analysis
  async getLandingPageAnalysis(accountId, dateRange = null, adNameFilter = null) {
    try {
      const dateCondition = dateRange || this.getDefaultDateRange();
      const adNameFilterClause = this.buildAdNameFilterClause(adNameFilter);
      
      const response = await apiClient.post('/api/chat/sql', {
        query: `
          SELECT 
            a.name as ad_name,
            CASE 
              WHEN il.id IS NOT NULL THEN 'Image'
              WHEN vl.id IS NOT NULL THEN 'Video'
              ELSE 'Unknown'
            END as ad_type,
            SUM(adp.impressions) as total_impressions,
            SUM(adp.clicks) as total_clicks,
            SUM(adp.spend) as total_spend,
            SUM(adp.purchases) as total_conversions,
            SUM(adp.purchase_value) as total_revenue,
            ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) as ctr,
            ROUND((SUM(adp.purchases)::float / NULLIF(SUM(adp.clicks), 0) * 100)::numeric, 4) as cvr,
            ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) as roas,
            -- Landing page indicators from creative features
            COALESCE(il.f_content_category, vl.cf_content_category) as content_category,
            COALESCE(il.f_value_proposition, vl.video_value_proposition) as value_proposition,
            COALESCE(il.f_call_to_action_type, vl.cf_call_to_action_type) as cta_type,
            COALESCE(il.f_price_display, vl.cf_price_display) as price_display,
            COALESCE(il.f_promotion_type, vl.cf_promotion_type) as promotion_type
          FROM t_ad a
          JOIN t_ad_daily_performance adp ON a.raw_ad_id = adp.raw_ad_id
          LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
          LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
          WHERE adp.raw_account_id = '${accountId}'
            AND ${dateCondition}
            ${adNameFilterClause}
          GROUP BY a.name, il.id, vl.id, il.f_content_category, vl.cf_content_category,
                   il.f_value_proposition, vl.video_value_proposition, il.f_call_to_action_type,
                   vl.cf_call_to_action_type, il.f_price_display, vl.cf_price_display,
                   il.f_promotion_type, vl.cf_promotion_type
          HAVING SUM(adp.impressions) >= 1000
          ORDER BY cvr DESC, roas DESC
        `,
        accountId: accountId
      });
      
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching landing page analysis:', error);
      return [];
    }
  },

  // Get copy and messaging analysis
  async getCopyAnalysis(accountId, dateRange = null, adNameFilter = null) {
    try {
      const dateCondition = dateRange || this.getDefaultDateRange();
      const adNameFilterClause = this.buildAdNameFilterClause(adNameFilter);
      
      const response = await apiClient.post('/api/chat/sql', {
        query: `
          SELECT 
            'Copy Content Analysis' as analysis_type,
            feature_name,
            feature_value,
            COUNT(DISTINCT a.raw_ad_id) as number_of_ads,
            SUM(adp.spend) as total_spend,
            SUM(adp.impressions) as total_impressions,
            SUM(adp.clicks) as total_clicks,
            SUM(adp.purchases) as total_conversions,
            SUM(adp.purchase_value) as total_revenue,
            ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) as ctr,
            ROUND((SUM(adp.purchases)::float / NULLIF(SUM(adp.clicks), 0) * 100)::numeric, 4) as cvr,
            ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) as roas
          FROM t_ad a
          JOIN t_ad_daily_performance adp ON a.raw_ad_id = adp.raw_ad_id
          LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
          LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
          CROSS JOIN LATERAL (
            VALUES 
              ('Headline', COALESCE(il.f_headline, '')),
              ('Primary Text', COALESCE(il.f_primary_text, '')),
              ('Description', COALESCE(il.f_description, '')),
              ('Audio Text Content', COALESCE(vl.cf_audio_text_content, '')),
              ('Text Overlay Content', COALESCE(vl.cf_text_overlay_content, '')),
              ('Visual Description', COALESCE(vl.cf_visual_description, '')),
              ('Clip Audio Text', COALESCE(vl.clip_audio_text, ''))
          ) AS copy_features(feature_name, feature_value)
          WHERE adp.raw_account_id = '${accountId}'
            AND ${dateCondition}
            ${adNameFilterClause}
            AND COALESCE(il.id, vl.id) IS NOT NULL
            AND copy_features.feature_value IS NOT NULL 
            AND copy_features.feature_value != ''
            AND LENGTH(TRIM(copy_features.feature_value)) > 10
          GROUP BY copy_features.feature_name, copy_features.feature_value
          HAVING SUM(adp.impressions) >= 1000 AND COUNT(DISTINCT a.raw_ad_id) >= 2
          ORDER BY roas DESC, total_spend DESC
        `,
        accountId: accountId
      });
      
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching copy analysis:', error);
      return [];
    }
  },

    // Get scale vs pause decision matrix
  async getScaleVsPauseMatrix(accountId, dateRange = null, adNameFilter = null) {
    try {
      const dateCondition = dateRange || this.getDefaultDateRange();
      const adNameFilterClause = this.buildAdNameFilterClause(adNameFilter);
      
      console.log(`🔍 Starting scale vs pause analysis for account: ${accountId}`);
      console.log(`🔍 Date condition: ${dateCondition}`);
      console.log(`🔍 Ad name filter: ${adNameFilterClause || 'None'}`);
      
      // First, let's check how many ads we have in total
      const countResponse = await apiClient.post('/api/chat/sql', {
        query: `
          SELECT 
            COUNT(DISTINCT a.raw_ad_id) as total_ads,
            COUNT(DISTINCT CASE WHEN il.id IS NOT NULL OR vl.id IS NOT NULL THEN a.raw_ad_id END) as ads_with_creative_labeling,
            COUNT(DISTINCT CASE WHEN SUM(adp.impressions) >= 1000 THEN a.raw_ad_id END) as ads_with_1000_impressions,
            COUNT(DISTINCT CASE WHEN SUM(adp.impressions) >= 100 THEN a.raw_ad_id END) as ads_with_100_impressions
          FROM t_ad a
          JOIN t_ad_daily_performance adp ON a.raw_ad_id = adp.raw_ad_id
          LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
          LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
          WHERE adp.raw_account_id = '${accountId}'
            AND ${dateCondition}
            ${adNameFilterClause}
          GROUP BY a.raw_ad_id
        `,
        accountId: accountId
      });
      
      console.log(`🔍 Ad count analysis:`, countResponse.data);
      
      const response = await apiClient.post('/api/chat/sql', {
        query: `
          SELECT 
            a.raw_ad_id,
            a.name as ad_name,
            CASE 
              WHEN il.id IS NOT NULL THEN 'Image'
              WHEN vl.id IS NOT NULL THEN 'Video'
              ELSE 'Unknown'
            END as ad_type,
            SUM(adp.impressions) as total_impressions,
            SUM(adp.clicks) as total_clicks,
            SUM(adp.spend) as total_spend,
            SUM(adp.purchases) as total_conversions,
            SUM(adp.purchase_value) as total_revenue,
            ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) as ctr,
            ROUND((SUM(adp.purchases)::float / NULLIF(SUM(adp.clicks), 0) * 100)::numeric, 4) as cvr,
            ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) as roas,
            ROUND((SUM(adp.spend)::float / NULLIF(SUM(adp.impressions), 0) * 1000)::numeric, 2) as cpm,
            -- Decision matrix calculation - More balanced thresholds
            CASE 
              WHEN ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) >= 2.5 
                   AND ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) >= 1.0
                THEN 'Scale'
              WHEN ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) < 1.0 
                   AND ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) < 0.5
                   AND SUM(adp.spend) > 50
                THEN 'Pause'
              ELSE 'Optimize'
            END as decision,
            -- Performance score (0-100) - Simplified to avoid nested CASE issues
            ROUND(
              (CASE WHEN ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) > 2.0 THEN 25 
                    WHEN ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) > 1.0 THEN 20 
                    WHEN ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) > 0.5 THEN 15 
                    ELSE 10 END) +
              (CASE WHEN ROUND((SUM(adp.purchases)::float / NULLIF(SUM(adp.clicks), 0) * 100)::numeric, 4) > 5.0 THEN 25
                    WHEN ROUND((SUM(adp.purchases)::float / NULLIF(SUM(adp.clicks), 0) * 100)::numeric, 4) > 2.0 THEN 20
                    WHEN ROUND((SUM(adp.purchases)::float / NULLIF(SUM(adp.clicks), 0) * 100)::numeric, 4) > 1.0 THEN 15
                    ELSE 10 END) +
              (CASE WHEN ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) > 3.0 THEN 25
                    WHEN ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) > 2.0 THEN 20
                    WHEN ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) > 1.0 THEN 15
                    ELSE 10 END) +
              (CASE WHEN SUM(adp.impressions) > 10000 THEN 25
                    WHEN SUM(adp.impressions) > 5000 THEN 20
                    WHEN SUM(adp.clicks) > 1000 THEN 15
                    ELSE 10 END)
            ) as performance_score
          FROM t_ad a
          JOIN t_ad_daily_performance adp ON a.raw_ad_id = adp.raw_ad_id
          LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
          LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
          WHERE adp.raw_account_id = '${accountId}'
            AND ${dateCondition}
            ${adNameFilterClause}
          GROUP BY a.raw_ad_id, a.name, il.id, vl.id
          HAVING SUM(adp.impressions) >= 50
          ORDER BY performance_score DESC, total_spend DESC
        `,
        accountId: accountId
      });
      
      console.log(`🔍 Scale vs pause response:`, response.data);
      
      if (response.data.data && response.data.data.length > 0) {
        console.log(`✅ Scale vs pause analysis: ${response.data.data.length} ads found`);
        return response.data.data;
      } else {
        console.log(`⚠️ No scale vs pause data found, trying simpler query...`);
        
        // Try a simpler query without complex decision matrix
        const simpleResponse = await apiClient.post('/api/chat/sql', {
          query: `
            SELECT 
              a.raw_ad_id,
              a.name as ad_name,
              CASE 
                WHEN il.id IS NOT NULL THEN 'Image'
                WHEN vl.id IS NOT NULL THEN 'Video'
                ELSE 'Unknown'
              END as ad_type,
              SUM(adp.impressions) as total_impressions,
              SUM(adp.clicks) as total_clicks,
              SUM(adp.spend) as total_spend,
              SUM(adp.purchases) as total_conversions,
              SUM(adp.purchase_value) as total_revenue,
              ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) as ctr,
              ROUND((SUM(adp.purchases)::float / NULLIF(SUM(adp.clicks), 0) * 100)::numeric, 4) as cvr,
              ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) as roas,
              ROUND((SUM(adp.spend)::float / NULLIF(SUM(adp.impressions), 0) * 1000)::numeric, 2) as cpm,
              -- Simple decision based on ROAS only - More balanced thresholds
              CASE 
                WHEN ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) >= 2.5 THEN 'Scale'
                WHEN ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) < 1.0 THEN 'Pause'
                ELSE 'Optimize'
              END as decision,
              -- Simple performance score
              ROUND(
                (CASE WHEN ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) > 2.0 THEN 50 ELSE
                 CASE WHEN ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) > 1.0 THEN 30 ELSE 10 END END
              ) as performance_score
            FROM t_ad a
            JOIN t_ad_daily_performance adp ON a.raw_ad_id = adp.raw_ad_id
            LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
            LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
            WHERE adp.raw_account_id = '${accountId}'
              AND ${dateCondition}
              ${adNameFilterClause}
            GROUP BY a.raw_ad_id, a.name, il.id, vl.id
            HAVING SUM(adp.impressions) >= 25
            ORDER BY total_spend DESC
          `,
          accountId: accountId
        });
        
        console.log(`🔍 Simple scale vs pause response:`, simpleResponse.data);
        
        if (simpleResponse.data.data && simpleResponse.data.data.length > 0) {
          console.log(`✅ Simple scale vs pause analysis: ${simpleResponse.data.data.length} ads found`);
          return simpleResponse.data.data;
        } else {
          console.log(`❌ Both scale vs pause queries failed`);
          return [];
        }
      }
    } catch (error) {
      console.error('Error fetching scale vs pause matrix:', error);
      return [];
    }
  },

  // Get funnel analysis data - Spend → Impressions → Clicks → ATC → Purchase → ROAS
  async getFunnelAnalysis(accountId, dateRange = null, adNameFilter = null) {
    try {
      const dateCondition = dateRange || this.getDefaultDateRange();
      const adNameFilterClause = this.buildAdNameFilterClause(adNameFilter);
      
      const response = await apiClient.post('/api/chat/sql', {
        query: `
          SELECT * FROM (
            SELECT 
              'Overall Funnel' AS funnel_type,
              'Spend' AS stage,
              SUM(base.spend) AS value,
              NULL::numeric AS rate,
              'currency' AS value_type
            FROM (
              SELECT adp.spend, adp.impressions, adp.clicks, adp.add_to_cart_num, adp.purchases, adp.purchase_value
              FROM t_ad_daily_performance adp
              JOIN t_ad a ON a.raw_ad_id = adp.raw_ad_id
              WHERE adp.raw_account_id = '${accountId}'
                AND ${dateCondition}
                ${adNameFilterClause}
            ) base

            UNION ALL

            SELECT
              'Overall Funnel',
              'Impressions',
              SUM(base.impressions),
              NULL::numeric,
              'number'
            FROM (
              SELECT adp.spend, adp.impressions, adp.clicks, adp.add_to_cart_num, adp.purchases, adp.purchase_value
              FROM t_ad_daily_performance adp
              JOIN t_ad a ON a.raw_ad_id = adp.raw_ad_id
              WHERE adp.raw_account_id = '${accountId}'
                AND ${dateCondition}
                ${adNameFilterClause}
            ) base

            UNION ALL

            SELECT
              'Overall Funnel',
              'Clicks',
              SUM(base.clicks),
              ROUND((SUM(base.clicks)::float / NULLIF(SUM(base.impressions), 0) * 100)::numeric, 2),
              'number'
            FROM (
              SELECT adp.spend, adp.impressions, adp.clicks, adp.add_to_cart_num, adp.purchases, adp.purchase_value
              FROM t_ad_daily_performance adp
              JOIN t_ad a ON a.raw_ad_id = adp.raw_ad_id
              WHERE adp.raw_account_id = '${accountId}'
                AND ${dateCondition}
                ${adNameFilterClause}
            ) base

            UNION ALL

            SELECT
              'Overall Funnel',
              'Add to Cart',
              SUM(COALESCE(base.add_to_cart_num, 0)),
              ROUND((SUM(COALESCE(base.add_to_cart_num, 0))::float / NULLIF(SUM(base.clicks), 0) * 100)::numeric, 2),
              'number'
            FROM (
              SELECT adp.spend, adp.impressions, adp.clicks, adp.add_to_cart_num, adp.purchases, adp.purchase_value
              FROM t_ad_daily_performance adp
              JOIN t_ad a ON a.raw_ad_id = adp.raw_ad_id
              WHERE adp.raw_account_id = '${accountId}'
                AND ${dateCondition}
                ${adNameFilterClause}
            ) base

            UNION ALL

            SELECT
              'Overall Funnel',
              'ATC to Purchase',
              SUM(base.purchases),
              ROUND((SUM(base.purchases)::float / NULLIF(SUM(COALESCE(base.add_to_cart_num, 0)), 0) * 100)::numeric, 2),
              'number'
            FROM (
              SELECT adp.spend, adp.impressions, adp.clicks, adp.add_to_cart_num, adp.purchases, adp.purchase_value
              FROM t_ad_daily_performance adp
              JOIN t_ad a ON a.raw_ad_id = adp.raw_ad_id
              WHERE adp.raw_account_id = '${accountId}'
                AND ${dateCondition}
                ${adNameFilterClause}
            ) base

            UNION ALL

            SELECT
              'Overall Funnel',
              'ROAS',
              ROUND((SUM(base.purchase_value)::float / NULLIF(SUM(base.spend), 0))::numeric, 2),
              NULL::numeric,
              'ratio'
            FROM (
              SELECT adp.spend, adp.impressions, adp.clicks, adp.add_to_cart_num, adp.purchases, adp.purchase_value
              FROM t_ad_daily_performance adp
              JOIN t_ad a ON a.raw_ad_id = adp.raw_ad_id
              WHERE adp.raw_account_id = '${accountId}'
                AND ${dateCondition}
                ${adNameFilterClause}
            ) base
          ) u
          ORDER BY
            CASE stage
              WHEN 'Spend' THEN 1
              WHEN 'Impressions' THEN 2
              WHEN 'Clicks' THEN 3
              WHEN 'Add to Cart' THEN 4
              WHEN 'ATC to Purchase' THEN 5
              WHEN 'ROAS' THEN 6
              ELSE 7
            END
        `,
        accountId: accountId
      });
      
      console.log(`🔍 Funnel analysis response:`, response.data);
      
      if (response.data.data && response.data.data.length > 0) {
        console.log(`✅ Funnel analysis: ${response.data.data.length} stages found`);
        return response.data.data;
      } else {
        console.log(`⚠️ No funnel data found, trying simpler query...`);
        
        // Fallback to basic funnel if the complex one fails
        const simpleResponse = await apiClient.post('/api/chat/sql', {
          query: `
            SELECT 
              'Basic Funnel' as funnel_type,
              'Spend' as stage,
              SUM(adp.spend) as value,
              NULL as rate,
              'currency' as value_type
            FROM t_ad_daily_performance adp
            WHERE adp.raw_account_id = '${accountId}'
              AND ${dateCondition}
              ${adNameFilterClause}
            
            UNION ALL
            
            SELECT 
              'Basic Funnel' as funnel_type,
              'Impressions' as stage,
              SUM(adp.impressions) as value,
              NULL as rate,
              'number' as value_type
            FROM t_ad_daily_performance adp
            WHERE adp.raw_account_id = '${accountId}'
              AND ${dateCondition}
              ${adNameFilterClause}
            
            UNION ALL
            
            SELECT 
              'Basic Funnel' as funnel_type,
              'Clicks' as stage,
              SUM(adp.clicks) as value,
              ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 2) as rate,
              'number' as value_type
            FROM t_ad_daily_performance adp
            WHERE adp.raw_account_id = '${accountId}'
              AND ${dateCondition}
              ${adNameFilterClause}
            
            UNION ALL
            
            SELECT 
              'Basic Funnel' as funnel_type,
              'Add to Cart' as stage,
              SUM(adp.add_to_cart_num) as value,
              ROUND((SUM(adp.add_to_cart_num)::float / NULLIF(SUM(adp.clicks), 0) * 100)::numeric, 2) as rate,
              'number' as value_type
            FROM t_ad_daily_performance adp
            WHERE adp.raw_account_id = '${accountId}'
              AND ${dateCondition}
              ${adNameFilterClause}
            
            UNION ALL
            
            SELECT 
              'Basic Funnel' as funnel_type,
              'Purchases' as stage,
              SUM(adp.purchases) as value,
              ROUND((SUM(adp.purchases)::float / NULLIF(SUM(adp.add_to_cart_num), 0) * 100)::numeric, 2) as rate,
              'number' as value_type
            FROM t_ad_daily_performance adp
            WHERE adp.raw_account_id = '${accountId}'
              AND ${dateCondition}
              ${adNameFilterClause}
            
            UNION ALL
            
            SELECT 
              'Basic Funnel' as funnel_type,
              'ROAS' as stage,
              ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) as value,
              NULL as rate,
              'ratio' as value_type
            FROM t_ad_daily_performance adp
            WHERE adp.raw_account_id = '${accountId}'
              AND ${dateCondition}
              ${adNameFilterClause}
          
          ORDER BY 
            CASE stage 
              WHEN 'Spend' THEN 1
              WHEN 'Impressions' THEN 2
              WHEN 'Clicks' THEN 3
              WHEN 'Purchases' THEN 4
              WHEN 'ROAS' THEN 5
              ELSE 6
            END
        `,
          accountId: accountId
        });
        
        console.log(`🔍 Simple funnel response:`, simpleResponse.data);
        
        if (simpleResponse.data.data && simpleResponse.data.data.length > 0) {
          console.log(`✅ Simple funnel analysis: ${simpleResponse.data.data.length} stages found`);
          return simpleResponse.data.data;
        } else {
          console.log(`❌ Both funnel queries failed`);
          return [];
        }
      }
    } catch (error) {
      console.error('Error fetching funnel analysis:', error);
      return [];
    }
  },

  // Get cross-account benchmarks for ad types using dedicated endpoint
  async getAdTypeBenchmarks(dateRange = null, adTypesFromAccount = []) {
    try {
      if (!adTypesFromAccount || adTypesFromAccount.length === 0) {
        console.log(`🔍 No ad types provided for benchmarks`);
        return [];
      }

      console.log(`🔍 Fetching benchmarks for ${adTypesFromAccount.length} ad types using dedicated endpoint`);
      
      // Extract raw date range for the benchmark query
      let rawDateRange = null;
      if (dateRange && typeof dateRange === 'string') {
        const intervalMatch = dateRange.match(/INTERVAL '(\d+) days?'/i);
        if (intervalMatch) {
          rawDateRange = `(CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '${intervalMatch[1]} days'`;
        }
      } else if (dateRange && typeof dateRange === 'number') {
        rawDateRange = `(CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '${dateRange} days'`;
      }

      const response = await apiClient.post('/api/dashboard/benchmarks/ad-types', {
        adTypes: adTypesFromAccount.map(item => ({
          ad_type: item.ad_type,
          media_type: item.media_type
        })),
        dateRange: rawDateRange
      });

      if (response.data && response.data.success && response.data.data) {
        const benchmarks = response.data.data.map(item => ({
          ad_type: item.ad_type,
          media_type: item.media_type,
          account_count: parseInt(item.account_count) || 0,
          total_ads: parseInt(item.total_ads) || 0,
          avg_ctr: parseFloat(item.avg_ctr) || 0,
          avg_cvr: parseFloat(item.avg_cvr) || 0,
          avg_roas: parseFloat(item.avg_roas) || 0,
          avg_cpm: parseFloat(item.avg_cpm) || 0,
          median_ctr: parseFloat(item.median_ctr) || 0,
          median_cvr: parseFloat(item.median_cvr) || 0,
          median_roas: parseFloat(item.median_roas) || 0
        }));

        console.log(`✅ Ad type benchmarks (cross-account): ${benchmarks.length} benchmarks found`);
        console.log(`🔍 Query info:`, response.data.query_info);
        return benchmarks;
      } else {
        console.log(`❌ No benchmark data found in response`);
        return [];
      }
    } catch (error) {
      console.error('Error fetching ad type benchmarks:', error);
      return [];
    }
  },

  // Get ad format comparison data
  async getAdFormatComparison(accountId, dateRange = null, adNameFilter = null) {
    try {
      const dateCondition = dateRange || this.getDefaultDateRange();
      const adNameFilterClause = this.buildAdNameFilterClause(adNameFilter);
      
      const response = await apiClient.post('/api/chat/sql', {
        query: `
          SELECT 
            COALESCE(a.ad_type, 'Unknown') as ad_format,
            CASE 
              WHEN il.id IS NOT NULL THEN 'Image' WHEN vl.id IS NOT NULL THEN 'Video' ELSE 'Unknown' END as media_type,
            COUNT(DISTINCT a.raw_ad_id) as number_of_ads,
            SUM(adp.impressions) as total_impressions,
            SUM(adp.clicks) as total_clicks,
            SUM(adp.spend) as total_spend,
            SUM(adp.purchases) as total_conversions,
            SUM(adp.purchase_value) as total_revenue,
            ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) as ctr,
            ROUND((SUM(adp.purchases)::float / NULLIF(SUM(adp.clicks), 0) * 100)::numeric, 4) as cvr,
            ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) as roas,
            ROUND((SUM(adp.spend)::float / NULLIF(SUM(adp.impressions), 0) * 1000)::numeric, 2) as cpm,
            -- Creative feature analysis by format
            COUNT(DISTINCT CASE WHEN COALESCE(il.f_bright_colors, vl.cf_bright_colors) = 1 THEN a.raw_ad_id END) as bright_colors_count,
            COUNT(DISTINCT CASE WHEN COALESCE(il.f_professional_style, vl.cf_professional_videography) = 1 THEN a.raw_ad_id END) as professional_style_count,
            COUNT(DISTINCT CASE WHEN COALESCE(il.f_ugc_style, vl.cf_ugc_amateur_videography) = 1 THEN a.raw_ad_id END) as ugc_style_count,
            COUNT(DISTINCT CASE WHEN COALESCE(il.f_face_present, vl.cf_creator_face_close_up) = 1 THEN a.raw_ad_id END) as face_present_count,
            COUNT(DISTINCT CASE WHEN COALESCE(il.f_product_shown, vl.cf_product_demonstrated) = 1 THEN a.raw_ad_id END) as product_shown_count,
            COUNT(DISTINCT CASE WHEN vl.cf_voiceover = 1 THEN a.raw_ad_id END) as voiceover_count,
            COUNT(DISTINCT CASE WHEN vl.cf_music_playing = 1 THEN a.raw_ad_id END) as music_playing_count
          FROM t_ad a
          JOIN t_ad_daily_performance adp ON a.raw_ad_id = adp.raw_ad_id
          LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
          LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
          WHERE adp.raw_account_id = '${accountId}'
            AND ${dateCondition}
            ${adNameFilterClause}
            AND COALESCE(il.id, vl.id) IS NOT NULL
          GROUP BY COALESCE(a.ad_type, 'Unknown'),
                   CASE WHEN il.id IS NOT NULL THEN 'Image' WHEN vl.id IS NOT NULL THEN 'Video' ELSE 'Unknown' END
          ORDER BY total_spend DESC
        `,
        accountId: accountId
      });
      
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching ad format comparison:', error);
      return [];
    }
  },

  // Get creator performance analysis
  async getCreatorPerformance(accountId, dateRange = null, adNameFilter = null) {
    try {
      const dateCondition = dateRange || this.getDefaultDateRange();
      const adNameFilterClause = this.buildAdNameFilterClause(adNameFilter);
      
      const response = await apiClient.post('/api/chat/sql', {
        query: `
          SELECT 
            'Creator Analysis' as analysis_type,
            feature_name,
            feature_value,
            COUNT(DISTINCT a.raw_ad_id) as number_of_ads,
            SUM(adp.spend) as total_spend,
            SUM(adp.impressions) as total_impressions,
            SUM(adp.clicks) as total_clicks,
            SUM(adp.purchases) as total_conversions,
            SUM(adp.purchase_value) as total_revenue,
            ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) as ctr,
            ROUND((SUM(adp.purchases)::float / NULLIF(SUM(adp.clicks), 0) * 100)::numeric, 4) as cvr,
            ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) as roas
          FROM t_ad a
          JOIN t_ad_daily_performance adp ON a.raw_ad_id = adp.raw_ad_id
          LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
          LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
          CROSS JOIN LATERAL (
            VALUES 
              ('Talent: Age Range', COALESCE(il.f_talent_age_range, vl.cf_talent_age_range, '')),
              ('Talent: Gender', COALESCE(il.f_talent_gender, vl.cf_talent_gender, '')),
              ('Talent: Type', COALESCE(il.f_talent_type, vl.cf_talent_type, '')),
              ('Talent: Actions', COALESCE(il.f_talent_actions, vl.cf_talent_actions, '')),
              ('Talent: Diversity', COALESCE(il.f_talent_diversity, vl.cf_talent_diversity, '')),
              ('Talent: Quantity', COALESCE(il.f_talent_quantity, vl.cf_talent_quantity, '')),
              ('Talent: Relationship', COALESCE(il.f_talent_relationship, vl.cf_talent_relationship, ''))
          ) AS creator_features(feature_name, feature_value)
          WHERE adp.raw_account_id = '${accountId}'
            AND ${dateCondition}
            ${adNameFilterClause}
            AND COALESCE(il.id, vl.id) IS NOT NULL
            AND creator_features.feature_value IS NOT NULL 
            AND creator_features.feature_value != ''
          GROUP BY creator_features.feature_name, creator_features.feature_value
          HAVING SUM(adp.impressions) >= 1000 AND COUNT(DISTINCT a.raw_ad_id) >= 2
          ORDER BY roas DESC, total_spend DESC
        `,
        accountId: accountId
      });
      
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching creator performance:', error);
      return [];
    }
  },

  // Get product performance analysis
  async getProductPerformance(accountId, dateRange = null, adNameFilter = null) {
    try {
      const dateCondition = dateRange || this.getDefaultDateRange();
      const adNameFilterClause = this.buildAdNameFilterClause(adNameFilter);
      
      const response = await apiClient.post('/api/chat/sql', {
        query: `
          SELECT 
            'Product Analysis' as analysis_type,
            feature_name,
            feature_value,
            COUNT(DISTINCT a.raw_ad_id) as number_of_ads,
            SUM(adp.spend) as total_spend,
            SUM(adp.impressions) as total_impressions,
            SUM(adp.clicks) as total_clicks,
            SUM(adp.purchases) as total_conversions,
            SUM(adp.purchase_value) as total_revenue,
            ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) as ctr,
            ROUND((SUM(adp.purchases)::float / NULLIF(SUM(adp.clicks), 0) * 100)::numeric, 4) as cvr,
            ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) as roas
          FROM t_ad a
          JOIN t_ad_daily_performance adp ON a.raw_ad_id = adp.raw_ad_id
          LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
          LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
          CROSS JOIN LATERAL (
            VALUES 
              ('Product: Category', COALESCE(il.f_product_category, vl.video_product_category, '')),
              ('Product: Position', COALESCE(il.f_product_position, vl.cf_product_position, '')),
              ('Product: Presentation', COALESCE(il.f_product_presentation, vl.cf_product_presentation, '')),
              ('Product: Shown', COALESCE(CASE WHEN il.f_product_shown = 1 THEN 'Yes' WHEN il.f_product_shown = 0 THEN 'No' ELSE 'Unknown' END, 
                         CASE WHEN vl.cf_product_demonstrated = 1 THEN 'Yes' WHEN vl.cf_product_demonstrated = 0 THEN 'No' ELSE 'Unknown' END, 'Unknown')),
              ('Product: Size Ratio', COALESCE(CASE WHEN il.f_product_size_ratio IS NOT NULL THEN ROUND(il.f_product_size_ratio::numeric, 2)::text ELSE 'Unknown' END, 'Unknown'))
          ) AS product_features(feature_name, feature_value)
          WHERE adp.raw_account_id = '${accountId}'
            AND ${dateCondition}
            ${adNameFilterClause}
            AND COALESCE(il.id, vl.id) IS NOT NULL
            AND product_features.feature_value IS NOT NULL 
            AND product_features.feature_value != ''
          GROUP BY product_features.feature_name, product_features.feature_value
          HAVING SUM(adp.impressions) >= 1000 AND COUNT(DISTINCT a.raw_ad_id) >= 2
          ORDER BY roas DESC, total_spend DESC
        `,
        accountId: accountId
      });
      
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching product performance:', error);
      return [];
    }
  },

  // Get LLM-powered copy content clustering and analysis
  async getCopyContentClustering(accountId, dateRange = null, adNameFilter = null) {
    try {
      // First get the copy content data
      const copyContentData = await this.getCopyContentForClustering(accountId, dateRange, adNameFilter);
      
      if (!copyContentData || copyContentData.length === 0) {
        return { 
          clusters: [], 
          insights: [], 
          recommendations: [],
          copyThemes: [],
          performancePatterns: []
        };
      }

      // Include ALL ads for clustering - even those without creative labeling data
      // Use ad name as fallback content for ads without creative labeling
      const validCopyData = copyContentData.filter(ad => {
        // Always include ads with spend > 0, regardless of creative content
        return parseFloat(ad.total_spend || 0) > 0;
      }).map(ad => {
        // Check if ad has real creative content (not just ad name repeated)
        const hasRealCreativeContent = (ad.headline && ad.headline.trim() && 
                                       ad.headline.trim() !== ad.ad_name && 
                                       ad.headline.trim().length > 10) ||
                                      (ad.primary_text && ad.primary_text.trim() && 
                                       ad.primary_text.trim() !== ad.ad_name && 
                                       ad.primary_text.trim().length > 10) ||
                                      (ad.description && ad.description.trim() && 
                                       ad.description.trim() !== ad.ad_name && 
                                       ad.description.trim().length > 10) ||
                                      (ad.audio_text && ad.audio_text.trim() && 
                                       ad.audio_text.trim() !== ad.ad_name && 
                                       ad.audio_text.trim().length > 10) ||
                                      (ad.text_overlay && ad.text_overlay.trim() && 
                                       ad.text_overlay.trim() !== ad.ad_name && 
                                       ad.text_overlay.trim().length > 10) ||
                                      (ad.visual_description && ad.visual_description.trim() && 
                                       ad.visual_description.trim() !== ad.ad_name && 
                                       ad.visual_description.trim().length > 10) ||
                                      (ad.clip_audio && ad.clip_audio.trim() && 
                                       ad.clip_audio.trim() !== ad.ad_name && 
                                       ad.clip_audio.trim().length > 10);
        
        if (!hasRealCreativeContent) {
          // For ads without real creative content, use empty strings instead of ad name
          return {
            ...ad,
            headline: '',
            primary_text: '',
            description: '',
            audio_text: '',
            text_overlay: '',
            visual_description: '',
            clip_audio: '',
            hasRealCreativeContent: false
          };
        }
        
        // For ads with real creative content, keep the original data
        return {
          ...ad,
          hasRealCreativeContent: true
        };
      });

      // Prioritize ads with real creative content for clustering
      const prioritizedCopyData = validCopyData.sort((a, b) => {
        // First priority: ads with assets (image/video labeling)
        const aHasAsset = !!(a.asset_id || a.image_labeling_id || a.video_labeling_id);
        const bHasAsset = !!(b.asset_id || b.image_labeling_id || b.video_labeling_id);
        
        if (aHasAsset && !bHasAsset) return -1;
        if (!aHasAsset && bHasAsset) return 1;
        
        // Second priority: ads with real creative content
        if (a.hasRealCreativeContent && !b.hasRealCreativeContent) return -1;
        if (!a.hasRealCreativeContent && b.hasRealCreativeContent) return 1;
        
        // Third priority: performance (ROAS)
        const aRoas = parseFloat(a.roas) || 0;
        const bRoas = parseFloat(b.roas) || 0;
        return bRoas - aRoas;
      });

      // Take all ads for clustering - no limit with new model capacity
      const topCopyData = prioritizedCopyData;
      
      const withCreativeContent = topCopyData.filter(ad => ad.hasRealCreativeContent).length;
      const withoutCreativeContent = topCopyData.filter(ad => !ad.hasRealCreativeContent).length;
      const withAssets = topCopyData.filter(ad => !!(ad.asset_id || ad.image_labeling_id || ad.video_labeling_id)).length;
      const withoutAssets = topCopyData.filter(ad => !(ad.asset_id || ad.image_labeling_id || ad.video_labeling_id)).length;
      
      console.log(`🎯 Copy Content Prioritization Results:`);
      console.log(`   - Total ads analyzed: ${validCopyData.length}`);
      console.log(`   - Ads with real creative content: ${withCreativeContent}`);
      console.log(`   - Ads without creative content: ${withoutCreativeContent}`);
      console.log(`   - Ads with assets: ${withAssets}`);
      console.log(`   - Ads without assets: ${withoutAssets}`);
      console.log(`   - Selected for clustering: ${topCopyData.length}`);

      // Prepare copy content for LLM analysis
      const copyAnalysisData = {
        totalAds: topCopyData.length,
        copySamples: topCopyData.map(ad => ({
          ad_name: ad.ad_name,
          headline: ad.headline || '',
          primary_text: ad.primary_text || '',
          description: ad.description || '',
          audio_text: ad.audio_text || '',
          text_overlay: ad.text_overlay || '',
          visual_description: ad.visual_description || '',
          clip_audio: ad.clip_audio || '',
          hasRealCreativeContent: ad.hasRealCreativeContent,
          asset_url: ad.asset_url || null,
          media_format: ad.media_format || null,
          hasAsset: !!(ad.asset_url && ad.asset_url.trim()),
          performance: {
            roas: ad.roas,
            ctr: ad.ctr,
            cvr: ad.cvr,
            spend: ad.total_spend,
            impressions: ad.total_impressions,
            conversions: ad.total_conversions
          }
        })),
        dateRange: dateRange || this.getDefaultDateRange()
      };

      console.log(`🔍 Raw copy analysis data length:`, copyAnalysisData.copySamples.length);
      console.log(`🔍 Valid copy data length after filtering:`, topCopyData.length);
      console.log(`🔍 Sample raw data:`, copyAnalysisData.copySamples.slice(0, 2));
      console.log(`🔍 Sample valid data:`, topCopyData.slice(0, 2));
      
      if (topCopyData.length === 0) {
        console.log(`🚨 No valid copy data found after filtering`);
        return { 
          clusters: [], 
          insights: [], 
          recommendations: [],
          copyThemes: [],
          performancePatterns: []
        };
      }

      console.log(`🤖 Sending ${topCopyData.length} copy samples to LLM for clustering analysis...`);
      console.log(`🔍 First few copy samples:`, topCopyData.slice(0, 3));

      // Send to LLM for copy clustering analysis with timeout
      const response = await Promise.race([
        apiClient.post('/api/chat/analyze', {
        message: `**TASK: CLUSTER INDIVIDUAL ADS BY COPY CONTENT - EFFICIENT MAPPING APPROACH**

You have ${topCopyData.length} individual ads to cluster. Your job is to assign each ad to a category and return a mapping.

**WHAT TO DO:**
1. **EACH AD MUST BE ASSIGNED TO A CATEGORY** - No ad left behind
2. **Create 3-15 broad categories** that can accommodate ALL ads (optimal range for detailed analysis)
3. **Map each ad index to a category_id** in the ad_category_mapping
4. **Aim for 100% coverage** - all ${topCopyData.length} ads must be assigned to categories

**CLUSTERING APPROACH:**
- Use broad, inclusive themes
- Group ads even with minimal similarity (single word, tone, or context)
- Create catch-all categories for any remaining ads
- Force 100% coverage - every ad MUST be in a category

**REQUIRED OUTPUT STRUCTURE:**
Return categories list and mapping:
- categories: Array of category objects with category_id, theme_name, description
- ad_category_mapping: Object mapping ad_index to category_id
- insights: Array of key insights about the clustering
- recommendations: Array of actionable recommendations

**CATEGORY STRUCTURE:**
Each category must have:
- category_id: unique integer identifier (0, 1, 2, etc.)
- theme_name: broad theme description
- description: what this category represents

**EXAMPLE OUTPUT FORMAT:**
\`\`\`json
{
  "categories": [
    {
      "category_id": 0,
      "theme_name": "Product Benefits & Features",
      "description": "Ads focusing on product functionality and benefits"
    },
    {
      "category_id": 1,
      "theme_name": "Urgency & Scarcity",
      "description": "Copy focusing on limited time offers and scarcity"
    }
  ],
  "ad_category_mapping": {
    "0": 0,
    "1": 0,
    "2": 1,
    "3": 1,
    "4": 0
  },
  "insights": [
    "Outlier: 'Problem-Solution' copy achieves 3.2x higher ROAS (4.8x vs 1.5x average) despite lower CTR",
    "Anomaly: Ads with 50+ word copy outperform short copy by 40% ROAS, challenging conventional best practices"
  ],
  "recommendations": [
    "Scale the 'Problem-Solution' format from top performer - potential 200% ROAS lift",
    "Test longer copy formats (40-60 words) on current short-copy campaigns showing <2x ROAS"
  ]
}
\`\`\`

**COPY EXAMPLE SELECTION RULES:**
1. PRIORITIZE ads with hasAsset=true (those with actual images/videos)
2. Select 3-5 examples per cluster (not just 1-2)
3. If no ads with assets available, select highest performing ads in cluster
4. Include variety in copy styles within each cluster

**MANDATORY REQUIREMENTS:**
- ✅ Include ALL ${topCopyData.length} ads in the mapping
- ✅ Each ad must be assigned to exactly one category
- ✅ Map ad_index to category_id for every ad
- ✅ Use broad, inclusive themes to accommodate all ads
- ✅ Create catch-all categories if needed
- ✅ NO AD left behind - force clustering if needed

Copy Content Data: ${JSON.stringify(copyAnalysisData, null, 2)}

Please provide a structured analysis with 3-15 categories (optimal range for detailed analysis) and map every single ad to a category. The response must include the categories list and complete ad-to-category mapping.`,
          accountId: accountId,
          context: 'copy_content_clustering_analysis'
        }),
        // Timeout after 30 minutes
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Copy clustering analysis timed out after 30 minutes')), 1800000)
        )
      ]);

      console.log('🤖 LLM copy clustering response received:', response.data);
      console.log('🔍 Raw response data type:', typeof response.data);
      console.log('🔍 Raw response keys:', Object.keys(response.data));

      // First, let's analyze the response structure to understand where data is located
      const llmData = response.data;
      console.log('🔍 RESPONSE STRUCTURE ANALYSIS:');
      console.log('  - Top level keys:', Object.keys(llmData));
      console.log('  - llmData.clusters:', llmData.clusters);
      console.log('  - llmData.fullData:', llmData.fullData);
      console.log('  - llmData.data:', llmData.data);
      console.log('  - llmData.result:', llmData.result);
      console.log('  - llmData.rawResponse exists:', !!llmData.rawResponse);
      
      if (llmData.fullData) {
        console.log('  - fullData keys:', Object.keys(llmData.fullData));
        console.log('  - fullData.clusters:', llmData.fullData.clusters);
      }
      
      if (llmData.rawResponse) {
        console.log('  - rawResponse preview:', llmData.rawResponse.substring(0, 200) + '...');
      }
      
      // Now let's try to find where the actual data is located
      let clusters = [];
      let insights = [];
      let recommendations = [];
      let copyThemes = [];
      let performancePatterns = [];
      
      // Try to extract data from various possible response structures
      if (llmData.clusters && Array.isArray(llmData.clusters)) {
        clusters = llmData.clusters;
      } else if (llmData.data && llmData.data.clusters && Array.isArray(llmData.data.clusters)) {
        clusters = llmData.data.clusters;
      } else if (llmData.result && llmData.result.clusters && Array.isArray(llmData.result.clusters)) {
        clusters = llmData.result.clusters;
      } else if (llmData.rawResponse) {
        // Try to parse rawResponse if it's JSON
        try {
          const parsedRaw = JSON.parse(llmData.rawResponse);
          if (parsedRaw.clusters && Array.isArray(parsedRaw.clusters)) {
            clusters = parsedRaw.clusters;
          }
        } catch (e) {
          console.log('🤖 rawResponse is not JSON, skipping');
        }
      }
      
      // Extract insights
      if (llmData.insights && Array.isArray(llmData.insights)) {
        console.log('✅ Found insights at llmData.insights');
        insights = llmData.insights;
      } else if (llmData.fullData && llmData.fullData.insights && Array.isArray(llmData.fullData.insights)) {
        console.log('✅ Found insights at llmData.fullData.insights');
        insights = llmData.fullData.insights;
      } else if (llmData.data && llmData.data.insights && Array.isArray(llmData.data.insights)) {
        console.log('✅ Found insights at llmData.data.insights');
        insights = llmData.data.insights;
      } else if (llmData.result && llmData.result.insights && Array.isArray(llmData.result.insights)) {
        console.log('✅ Found insights at llmData.result.insights');
        insights = llmData.result.insights;
      }
      
      // Extract recommendations
      if (llmData.recommendations && Array.isArray(llmData.recommendations)) {
        console.log('✅ Found recommendations at llmData.recommendations');
        recommendations = llmData.recommendations;
      } else if (llmData.fullData && llmData.fullData.recommendations && Array.isArray(llmData.fullData.recommendations)) {
        console.log('✅ Found recommendations at llmData.fullData.recommendations');
        recommendations = llmData.fullData.recommendations;
      } else if (llmData.data && llmData.data.recommendations && Array.isArray(llmData.data.recommendations)) {
        console.log('✅ Found recommendations at llmData.data.recommendations');
        recommendations = llmData.data.recommendations;
      } else if (llmData.result && llmData.result.recommendations && Array.isArray(llmData.result.recommendations)) {
        console.log('✅ Found recommendations at llmData.result.recommendations');
        recommendations = llmData.result.recommendations;
      }
      
      // Extract copyThemes
      if (llmData.copyThemes && Array.isArray(llmData.copyThemes)) {
        copyThemes = llmData.copyThemes;
      } else if (llmData.data && llmData.data.copyThemes && Array.isArray(llmData.data.copyThemes)) {
        copyThemes = llmData.data.copyThemes;
      } else if (llmData.result && llmData.result.copyThemes && Array.isArray(llmData.result.copyThemes)) {
        copyThemes = llmData.result.copyThemes;
      }
      
      // Extract performancePatterns
      if (llmData.performancePatterns && Array.isArray(llmData.performancePatterns)) {
        performancePatterns = llmData.performancePatterns;
      } else if (llmData.data && llmData.data.performancePatterns && Array.isArray(llmData.data.performancePatterns)) {
        performancePatterns = llmData.data.performancePatterns;
      } else if (llmData.result && llmData.result.performancePatterns && Array.isArray(llmData.result.performancePatterns)) {
        performancePatterns = llmData.result.performancePatterns;
      }
      
      console.log('🤖 Flexible parsing results:', {
        clustersFound: clusters.length,
        insightsFound: insights.length,
        recommendationsFound: recommendations.length,
        copyThemesFound: copyThemes.length,
        performancePatternsFound: performancePatterns.length,
        responseKeys: Object.keys(llmData),
        hasRawResponse: !!llmData.rawResponse
      });
      
      // Calculate coverage
      const totalAdsInClusters = clusters.reduce((sum, cluster) => sum + (cluster.ads?.length || 0), 0);
      
      // Final coverage summary
      const finalCoverage = ((totalAdsInClusters / topCopyData.length) * 100).toFixed(1);
      console.log(`🎯 FINAL COVERAGE SUMMARY: ${totalAdsInClusters}/${topCopyData.length} ads clustered (${finalCoverage}%)`);
      if (parseFloat(finalCoverage) < 90) {
        console.log(`🚨 COVERAGE BELOW 90% - LLM needs to be more aggressive!`);
        console.log(`📊 Current: ${finalCoverage}% | Target: 90%+ | Missing: ${topCopyData.length - totalAdsInClusters} ads`);
        console.log(`💡 Consider: broader themes, more catch-all categories, force inclusion`);
      } else if (parseFloat(finalCoverage) < 80) {
        console.log(`⚠️  Coverage below 80% - clustering too selective`);
      } else {
        console.log(`✅ Excellent coverage achieved!`);
      }

      // Post-process clusters to recalculate performance metrics from actual ad data
      const processedClusters = clusters.map(cluster => {
        if (!cluster.ads || !Array.isArray(cluster.ads) || cluster.ads.length === 0) {
          return cluster;
        }

        // Find actual ad data for ads in this cluster
        const clusterAdData = cluster.ads.map(adName => 
          topCopyData.find(ad => ad.ad_name === adName)
        ).filter(ad => ad); // Remove any undefined entries

        if (clusterAdData.length === 0) {
          return cluster;
        }

        // Recalculate performance metrics from actual ad data
        const totalSpend = clusterAdData.reduce((sum, ad) => sum + parseFloat(ad.total_spend || 0), 0);
        const totalRevenue = clusterAdData.reduce((sum, ad) => sum + parseFloat(ad.total_revenue || 0), 0);
        const totalImpressions = clusterAdData.reduce((sum, ad) => sum + parseFloat(ad.total_impressions || 0), 0);
        const totalClicks = clusterAdData.reduce((sum, ad) => sum + parseFloat(ad.total_clicks || 0), 0);
        const totalConversions = clusterAdData.reduce((sum, ad) => sum + parseFloat(ad.total_conversions || 0), 0);

        // Calculate averages and rates
        const avgRoas = totalSpend > 0 ? totalRevenue / totalSpend : 0;
        const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
        const avgCvr = totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0;

        // Update cluster with recalculated performance BUT preserve original copy example performance data
        return {
          ...cluster,
          performance: {
            avg_roas: parseFloat(avgRoas.toFixed(2)),
            avg_ctr: parseFloat(avgCtr.toFixed(2)),
            avg_cvr: avgCvr > 0 ? parseFloat(avgCvr.toFixed(2)) : null, // Use null instead of 0 for better N/A handling
            total_spend: parseFloat(totalSpend.toFixed(2)),
            total_revenue: parseFloat(totalRevenue.toFixed(2)),
            total_impressions: totalImpressions,
            total_clicks: totalClicks,
            total_conversions: totalConversions,
            ads_count: clusterAdData.length
          },
          // Use ad name to lookup actual performance metrics from our data with partial matching
          copy_examples: cluster.copy_examples?.map(example => {
            // Try exact match first
            let actualAdData = topCopyData.find(ad => ad.ad_name === example.ad_name);
            
            // If exact match fails, try partial matching (LLM often returns prefixes)
            if (!actualAdData) {
              const exampleName = example.ad_name.toLowerCase();
              actualAdData = topCopyData.find(ad => {
                const adName = ad.ad_name.toLowerCase();
                // Try multiple partial matching strategies
                return (
                  adName.includes(exampleName) ||  // LLM name is substring of actual name
                  exampleName.includes(adName) ||  // Actual name is substring of LLM name
                  adName.startsWith(exampleName) || // LLM name is prefix of actual name
                  exampleName.startsWith(adName)    // Actual name is prefix of LLM name
                );
              });
            }
            
            // If still no match, try fuzzy matching on first few words
            if (!actualAdData) {
              const exampleWords = example.ad_name.toLowerCase().split(/[\s\-_]+/).filter(w => w.length > 2);
              if (exampleWords.length > 0) {
                actualAdData = topCopyData.find(ad => {
                  const adWords = ad.ad_name.toLowerCase().split(/[\s\-_]+/);
                  // Match if first 2-3 significant words match
                  const matchCount = exampleWords.slice(0, 3).filter(word => 
                    adWords.some(adWord => adWord.includes(word) || word.includes(adWord))
                  ).length;
                  return matchCount >= Math.min(2, exampleWords.length);
                });
              }
            }
            
            return {
              ...example,
              // Update ad_name with the actual matched name for display
              ad_name: actualAdData ? actualAdData.ad_name : example.ad_name,
              performance: actualAdData ? {
                roas: actualAdData.roas ? parseFloat(actualAdData.roas) : null,
                ctr: actualAdData.ctr ? parseFloat(actualAdData.ctr) : null,
                cvr: actualAdData.cvr ? parseFloat(actualAdData.cvr) : null,
                spend: actualAdData.total_spend ? parseFloat(actualAdData.total_spend) : null,
                impressions: actualAdData.total_impressions ? parseInt(actualAdData.total_impressions) : null,
                conversions: actualAdData.total_conversions ? parseInt(actualAdData.total_conversions) : null
              } : {
                roas: null,
                ctr: null, 
                cvr: null,
                spend: null,
                impressions: null,
                conversions: null
              }
            };
          }) || []
        };
      });

      console.log('🔄 Post-processed cluster performance metrics:', processedClusters.map(c => ({
        theme: c.theme_name,
        ads: c.ads?.length || 0,
        performance: c.performance
      })));

      // Filter out clusters without assets for better copy analytics
      const clustersWithAssets = processedClusters.filter(cluster => {
        // Check if cluster has copy examples with assets
        const hasAssetExamples = cluster.copy_examples && cluster.copy_examples.some(ex => ex.hasAsset);
        return hasAssetExamples;
      });

      console.log(`🖼️ Filtered clusters: ${processedClusters.length} → ${clustersWithAssets.length} (kept only clusters with asset examples)`);

      // Send corrected metrics back to LLM for insight revision
      let revisedInsights = insights;
      let revisedRecommendations = recommendations;

      if (clustersWithAssets.length > 0) {
        try {
          const revisionData = {
            clusters: clustersWithAssets.map(cluster => ({
              theme_name: cluster.theme_name,
              description: cluster.description,
              ads_count: cluster.ads?.length || 0,
              performance: cluster.performance,
              top_examples: cluster.copy_examples?.slice(0, 3).map(ex => ({
                ad_name: ex.ad_name,
                performance: ex.performance
              })) || []
            }))
          };

          const revisionResponse = await apiClient.post('/api/chat/analyze', {
            message: `Based on these CORRECTED cluster performance metrics, revise the insights and recommendations to ensure accuracy:

CORRECTED CLUSTER DATA:
${JSON.stringify(revisionData, null, 2)}

ORIGINAL INSIGHTS:
${insights.map((insight, i) => `${i+1}. ${insight}`).join('\n')}

ORIGINAL RECOMMENDATIONS:
${recommendations.map((rec, i) => `${i+1}. ${rec}`).join('\n')}

Please provide ONLY a JSON response with:
{
  "insights": [...], // Revised insights using CORRECTED performance numbers
  "recommendations": [...] // Revised recommendations using CORRECTED performance numbers
}

Ensure all performance numbers (ROAS, CTR, CVR, spend) in insights and recommendations match the corrected cluster data above.`,
            accountId: accountId,
            context: 'insight_revision'
          });

          if (revisionResponse.data && revisionResponse.data.insights && revisionResponse.data.recommendations) {
            revisedInsights = revisionResponse.data.insights;
            revisedRecommendations = revisionResponse.data.recommendations;
            console.log('✅ Successfully revised insights and recommendations with corrected metrics');
          }
        } catch (revisionError) {
          console.warn('⚠️ Failed to revise insights, using original:', revisionError.message);
        }
      }

      const result = {
        clusters: clustersWithAssets,
        insights: revisedInsights,
        recommendations: revisedRecommendations,
        copyThemes: copyThemes,
        performancePatterns: performancePatterns,
        totalSamples: topCopyData.length,
        rawCopyData: copyContentData,
        clusterPerformance: [],
        adToClusterMapping: {},
        analysisTimestamp: new Date().toISOString(),
        rawResponse: llmData.rawResponse || 'No raw response'
      };
      
      return result;
    } catch (error) {
      console.error('Error getting copy content clustering:', error);
            return { 
        clusters: [], 
        insights: [],
        recommendations: [],
        copyThemes: [],
        performancePatterns: [],
        totalSamples: 0,
        rawCopyData: [],
        clusterPerformance: [],
        adToClusterMapping: {},
        error: error.message
      };
    }
  },





  // Get top performers analysis in the expected format (byROAS and bySpend)
  async getTopPerformersAnalysisFormatted(accountId, dateRange = null, adNameFilter = null) {
    try {
      // Get top performers by ROAS
      const topPerformersByROAS = await this.getTopPerformersAnalysis(accountId, dateRange, adNameFilter, 'roas');
      
      // Get top performers by spend
      const topPerformersBySpend = await this.getTopPerformersAnalysis(accountId, dateRange, adNameFilter, 'spend');
      
      // Return in the expected format
      return {
        byROAS: topPerformersByROAS || [],
        bySpend: topPerformersBySpend || []
      };
    } catch (error) {
      console.error('Error getting formatted top performers analysis:', error);
      return {
        byROAS: [],
        bySpend: []
      };
    }
  },

  // Get creative feature correlations using proven correlation queries
  async getCreativeFeatureCorrelations(accountId, dateRange = null, adNameFilter = null) {
    try {
      const dateCondition = dateRange || `d.date >= (CURRENT_DATE AT TIME ZONE 'America/Los_Angeles')::date - INTERVAL '30 days'`;
      const adNameFilterClause = this.buildAdNameFilterClause(adNameFilter);
      
      console.log(`🔍 Starting creative feature correlation analysis for account: ${accountId}`);
      
      // Static/Image correlation query (exact from user)
      const staticCorrelationQuery = `
        WITH perf AS (
          SELECT
            a.asset_id,
            a.raw_ad_id,
            a.raw_account_id,
            CASE WHEN SUM(d.spend) > 0 THEN SUM(d.purchase_value)::float / SUM(d.spend)::float ELSE 0 END AS roas,
            SUM(d.spend)::float AS spend
          FROM t_ad a
          JOIN t_ad_daily_performance d ON d.raw_ad_id = a.raw_ad_id
          WHERE a.raw_account_id = '${accountId}'
            AND ${dateCondition}
            ${adNameFilterClause}
          GROUP BY a.asset_id, a.raw_ad_id, a.raw_account_id
          HAVING SUM(d.spend) > 0
        ),
        base AS (
          SELECT f.*, p.roas, p.spend
          FROM t_ad_image_labelings f
          JOIN perf p ON p.asset_id = f.raw_asset_id
          WHERE f.raw_account_id = '${accountId}' AND p.roas IS NOT NULL
        ),
        corrs_flags AS (
          SELECT feature, level,
                 corr(indicator, roas) AS r,
                 AVG(roas) FILTER (WHERE level = 'present' AND indicator = 1.0) AS avg_roas,
                 SUM(spend) FILTER (WHERE level = 'present' AND indicator = 1.0) AS total_spend
          FROM (
            SELECT 'f_face_present' AS feature, 'present' AS level, CASE WHEN f_face_present > 0 THEN 1.0 ELSE 0.0 END AS indicator, roas, spend FROM base UNION ALL
            SELECT 'f_face_present', 'absent', CASE WHEN f_face_present = 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'f_bright_colors', 'present', CASE WHEN f_bright_colors > 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'f_bright_colors', 'absent', CASE WHEN f_bright_colors = 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'f_product_shown', 'present', CASE WHEN f_product_shown > 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'f_product_shown', 'absent', CASE WHEN f_product_shown = 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'f_cta_presence', 'present', CASE WHEN f_cta_presence > 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'f_cta_presence', 'absent', CASE WHEN f_cta_presence = 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'f_professional_style', 'present', CASE WHEN f_professional_style > 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'f_professional_style', 'absent', CASE WHEN f_professional_style = 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'f_direct_gaze', 'present', CASE WHEN f_direct_gaze > 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'f_direct_gaze', 'absent', CASE WHEN f_direct_gaze = 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'f_emotional_expression', 'present', CASE WHEN f_emotional_expression > 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'f_emotional_expression', 'absent', CASE WHEN f_emotional_expression = 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base
          ) z
          GROUP BY feature, level
        )
        SELECT feature, level, r AS correlation, CASE WHEN r >= 0 THEN 'positive' ELSE 'negative' END AS direction, avg_roas, total_spend, 'static' as media_format
        FROM corrs_flags
        WHERE r IS NOT NULL
        ORDER BY ABS(r) DESC
        LIMIT 50
      `;

      // Video correlation query (exact from user)  
      const videoCorrelationQuery = `
        WITH perf AS (
          SELECT
            a.asset_id,
            a.raw_ad_id,
            CASE WHEN SUM(d.spend) > 0 THEN SUM(d.purchase_value)::float / SUM(d.spend)::float ELSE 0 END AS roas,
            SUM(d.spend)::float AS spend
          FROM t_ad a
          JOIN t_ad_daily_performance d ON d.raw_ad_id = a.raw_ad_id
          WHERE a.raw_account_id = '${accountId}'
            AND ${dateCondition}
            ${adNameFilterClause}
          GROUP BY a.asset_id, a.raw_ad_id
          HAVING SUM(d.spend) > 0
        ),
        base AS (
          SELECT v.*, p.roas, p.spend
          FROM t_ad_video_labelings v
          JOIN perf p ON p.asset_id = v.raw_asset_id
          WHERE v.clip_sno = 1 AND v.raw_account_id = '${accountId}' AND p.roas IS NOT NULL
        ),
        corrs_flags AS (
          SELECT feature, level,
                 corr(indicator, roas) AS r,
                 AVG(roas) FILTER (WHERE level = 'present' AND indicator = 1.0) AS avg_roas,
                 SUM(spend) FILTER (WHERE level = 'present' AND indicator = 1.0) AS total_spend
          FROM (
            SELECT 'cf_voiceover' AS feature, 'present' AS level, CASE WHEN cf_voiceover > 0 THEN 1.0 ELSE 0.0 END AS indicator, roas, spend FROM base UNION ALL
            SELECT 'cf_voiceover', 'absent', CASE WHEN cf_voiceover = 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'cf_music_playing', 'present', CASE WHEN cf_music_playing > 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'cf_music_playing', 'absent', CASE WHEN cf_music_playing = 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'cf_creator_face_close_up', 'present', CASE WHEN cf_creator_face_close_up > 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'cf_creator_face_close_up', 'absent', CASE WHEN cf_creator_face_close_up = 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'cf_professional_videography', 'present', CASE WHEN cf_professional_videography > 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'cf_professional_videography', 'absent', CASE WHEN cf_professional_videography = 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'cf_product_demonstrated', 'present', CASE WHEN cf_product_demonstrated > 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base UNION ALL
            SELECT 'cf_product_demonstrated', 'absent', CASE WHEN cf_product_demonstrated = 0 THEN 1.0 ELSE 0.0 END, roas, spend FROM base
          ) z
          GROUP BY feature, level
        )
        SELECT feature, level, r AS correlation, CASE WHEN r >= 0 THEN 'positive' ELSE 'negative' END AS direction, avg_roas, total_spend, 'video' as media_format
        FROM corrs_flags
        WHERE r IS NOT NULL
        ORDER BY ABS(r) DESC
        LIMIT 50
      `;
      
      // Execute both queries in parallel
      const [staticResponse, videoResponse] = await Promise.all([
        apiClient.post('/api/chat/sql', {
          query: staticCorrelationQuery,
          accountId: accountId
        }),
        apiClient.post('/api/chat/sql', {
          query: videoCorrelationQuery,
          accountId: accountId
        })
      ]);
      
      const staticData = staticResponse.data.data || [];
      const videoData = videoResponse.data.data || [];
      
      console.log(`📊 Creative feature correlations loaded:`, {
        static: staticData.length,
        video: videoData.length
      });
      
      // Combine and return both datasets
      return {
        static: staticData,
        video: videoData,
        combined: [...staticData, ...videoData]
      };
      
    } catch (error) {
      console.error('Error getting creative feature correlations:', error);
      return {
        static: [],
        video: [],
        combined: []
      };
    }
  },

  // Generic grouping analysis - groups ads by a specific field
  async getGroupByAnalysis(accountId, groupByField, dateRange = null, adNameFilter = null, correlationMetric = 'roas') {
    try {
      const dateCondition = dateRange || this.getDefaultDateRange();
      const adNameFilterClause = this.buildAdNameFilterClause(adNameFilter);
      
      // Map groupByField to database field names
      const fieldMapping = {
        'asset-id': { field: 'a.asset_id', label: 'Asset ID', groupBy: 'a.asset_id' },
        'campaign-name': { field: 'COALESCE(c.name, \'Unknown Campaign\')', label: 'Campaign Name', groupBy: 'COALESCE(c.name, \'Unknown Campaign\')' },
        'setting': { 
          field: 'COALESCE(vl.cf_setting, il.f_setting, \'Unknown\')', 
          label: 'Setting', 
          groupBy: 'COALESCE(vl.cf_setting, il.f_setting, \'Unknown\')' 
        },
        'dominant-color': { 
          field: 'COALESCE(vl.cf_dominant_color, il.f_dominant_color, \'Unknown\')', 
          label: 'Dominant Color', 
          groupBy: 'COALESCE(vl.cf_dominant_color, il.f_dominant_color, \'Unknown\')' 
        },
        'concept': {
          field: 'fbaa.concept',
          label: 'Concept',
          groupBy: 'fbaa.concept'
        },
        'value-proposition': {
          field: 'COALESCE(vl.video_value_proposition, il.f_value_proposition, \'Unknown\')',
          label: 'Value Proposition',
          groupBy: 'COALESCE(vl.video_value_proposition, il.f_value_proposition, \'Unknown\')'
        },
        'content-category': {
          field: 'COALESCE(vl.cf_content_category, il.f_content_category, \'Unknown\')',
          label: 'Content Category',
          groupBy: 'COALESCE(vl.cf_content_category, il.f_content_category, \'Unknown\')'
        },
        'talent-quantity': {
          field: 'COALESCE(vl.cf_talent_quantity, il.f_talent_quantity, \'Unknown\')',
          label: 'Number of Talent',
          groupBy: 'COALESCE(vl.cf_talent_quantity, il.f_talent_quantity, \'Unknown\')'
        },
        'talent-gender': {
          field: 'COALESCE(vl.cf_talent_gender, il.f_talent_gender, \'Unknown\')',
          label: 'Male vs Female',
          groupBy: 'COALESCE(vl.cf_talent_gender, il.f_talent_gender, \'Unknown\')'
        },
        'talent-age': {
          field: 'COALESCE(vl.cf_talent_age_range, il.f_talent_age_range, \'Unknown\')',
          label: 'Age Range',
          groupBy: 'COALESCE(vl.cf_talent_age_range, il.f_talent_age_range, \'Unknown\')'
        },
        'music-playing': {
          field: 'CASE WHEN vl.cf_music_playing = 1 THEN \'Yes\' ELSE \'No\' END',
          label: 'Music Playing',
          groupBy: 'CASE WHEN vl.cf_music_playing = 1 THEN \'Yes\' ELSE \'No\' END'
        },
        'music-genre': {
          field: 'COALESCE(vl.cf_music_genre, \'Unknown\')',
          label: 'Music Genre',
          groupBy: 'COALESCE(vl.cf_music_genre, \'Unknown\')'
        },
        'voiceover': {
          field: 'CASE WHEN vl.cf_voiceover = 1 THEN \'Yes\' ELSE \'No\' END',
          label: 'Voiceover',
          groupBy: 'CASE WHEN vl.cf_voiceover = 1 THEN \'Yes\' ELSE \'No\' END'
        },
        'video-length-0-14s': {
          field: 'CASE WHEN vl.video_duration <= 14 THEN \'0-14s\' ELSE NULL END',
          label: '0-14s',
          groupBy: 'CASE WHEN vl.video_duration <= 14 THEN \'0-14s\' ELSE NULL END',
          filter: 'AND vl.video_duration <= 14'
        },
        'video-length-15-29s': {
          field: 'CASE WHEN vl.video_duration > 14 AND vl.video_duration <= 29 THEN \'15-29s\' ELSE NULL END',
          label: '15-29s',
          groupBy: 'CASE WHEN vl.video_duration > 14 AND vl.video_duration <= 29 THEN \'15-29s\' ELSE NULL END',
          filter: 'AND vl.video_duration > 14 AND vl.video_duration <= 29'
        },
        'video-length-30-59s': {
          field: 'CASE WHEN vl.video_duration > 29 AND vl.video_duration <= 59 THEN \'30-59s\' ELSE NULL END',
          label: '30-59s',
          groupBy: 'CASE WHEN vl.video_duration > 29 AND vl.video_duration <= 59 THEN \'30-59s\' ELSE NULL END',
          filter: 'AND vl.video_duration > 29 AND vl.video_duration <= 59'
        },
        'video-length-1m-1.5m': {
          field: 'CASE WHEN vl.video_duration > 59 AND vl.video_duration <= 90 THEN \'1m-1.5m\' ELSE NULL END',
          label: '1m-1.5m',
          groupBy: 'CASE WHEN vl.video_duration > 59 AND vl.video_duration <= 90 THEN \'1m-1.5m\' ELSE NULL END',
          filter: 'AND vl.video_duration > 59 AND vl.video_duration <= 90'
        },
        'video-length-1.5m-2m': {
          field: 'CASE WHEN vl.video_duration > 90 AND vl.video_duration <= 120 THEN \'1.5m-2m\' ELSE NULL END',
          label: '1.5m-2m',
          groupBy: 'CASE WHEN vl.video_duration > 90 AND vl.video_duration <= 120 THEN \'1.5m-2m\' ELSE NULL END',
          filter: 'AND vl.video_duration > 90 AND vl.video_duration <= 120'
        },
        'video-length-2m-5m': {
          field: 'CASE WHEN vl.video_duration > 120 AND vl.video_duration <= 300 THEN \'2m-5m\' ELSE NULL END',
          label: '2m-5m',
          groupBy: 'CASE WHEN vl.video_duration > 120 AND vl.video_duration <= 300 THEN \'2m-5m\' ELSE NULL END',
          filter: 'AND vl.video_duration > 120 AND vl.video_duration <= 300'
        },
        'video-length-5m+': {
          field: 'CASE WHEN vl.video_duration > 300 THEN \'5m+\' ELSE NULL END',
          label: '5m+',
          groupBy: 'CASE WHEN vl.video_duration > 300 THEN \'5m+\' ELSE NULL END',
          filter: 'AND vl.video_duration > 300'
        }
      };

      const mapping = fieldMapping[groupByField];
      if (!mapping) {
        throw new Error(`Unknown groupByField: ${groupByField}`);
      }

      // Special handling for concept (from Frost Buddy asset analysis table)
      let conceptJoin = '';
      if (groupByField === 'concept') {
        conceptJoin = `LEFT JOIN t_frost_buddy_asset_analysis fbaa ON fbaa.asset_id = a.asset_id AND fbaa.account_id = '${accountId}'`;
      } else {
        conceptJoin = '';
      }

      const response = await apiClient.post('/api/chat/sql', {
        query: `
          SELECT 
            ${mapping.field} as group_value,
            CASE 
              WHEN il.id IS NOT NULL THEN 'Image' 
              WHEN vl.id IS NOT NULL THEN 'Video' 
              ELSE 'Unknown' 
            END as media_type,
            ARRAY_AGG(DISTINCT a.raw_ad_id) as ad_ids,
            COUNT(DISTINCT a.raw_ad_id) as number_of_ads,
            SUM(adp.impressions) as total_impressions,
            SUM(adp.clicks) as total_clicks,
            SUM(adp.spend) as total_spend,
            SUM(adp.purchases) as total_conversions,
            SUM(adp.purchase_value) as total_revenue,
            ROUND((SUM(adp.clicks)::float / NULLIF(SUM(adp.impressions), 0) * 100)::numeric, 4) as ctr,
            ROUND((SUM(adp.purchases)::float / NULLIF(SUM(adp.clicks), 0) * 100)::numeric, 4) as cvr,
            ROUND((SUM(adp.purchase_value)::float / NULLIF(SUM(adp.spend), 0))::numeric, 2) as roas,
            ROUND((SUM(adp.spend)::float / NULLIF(SUM(adp.impressions), 0) * 1000)::numeric, 2) as cpm,
            ROUND((SUM(adp.spend)::float / NULLIF(SUM(adp.purchases), 0))::numeric, 2) as cpa,
            COUNT(DISTINCT adp.date) as active_days
          FROM t_ad a
          JOIN t_ad_daily_performance adp ON a.raw_ad_id = adp.raw_ad_id
          LEFT JOIN t_ad_campaign c ON a.raw_campaign_id = c.raw_campaign_id
          LEFT JOIN t_ad_image_labelings il ON il.raw_asset_id = a.asset_id AND il.raw_account_id = '${accountId}'
          LEFT JOIN t_ad_video_labelings vl ON vl.raw_asset_id = a.asset_id AND vl.clip_sno = 1 AND vl.raw_account_id = '${accountId}'
          ${conceptJoin}
          WHERE adp.raw_account_id = '${accountId}'
            AND ${dateCondition}
            ${adNameFilterClause}
            ${mapping.filter || ''}
            ${groupByField === 'concept' ? 'AND fbaa.concept IS NOT NULL AND fbaa.concept != \'\'' : ''}
          GROUP BY ${mapping.field}, 
                   CASE 
                     WHEN il.id IS NOT NULL THEN 'Image' 
                     WHEN vl.id IS NOT NULL THEN 'Video' 
                     ELSE 'Unknown' 
                   END
          HAVING SUM(adp.impressions) >= 100
            AND ${mapping.field} IS NOT NULL
            ${correlationMetric === 'cpa' ? 'AND SUM(adp.purchases) > 0' : ''}
          ORDER BY ${correlationMetric === 'revenue' ? 'total_revenue DESC' :
                     correlationMetric === 'ctr' ? 'ctr DESC' :
                     correlationMetric === 'spend' ? 'total_spend DESC' :
                     correlationMetric === 'cpa' ? 'cpa ASC' :
                     'roas DESC'}
        `,
        accountId: accountId
      });

      if (response.data.data && response.data.data.length > 0) {
        return response.data.data.map(item => ({
          ...item,
          group_label: mapping.label,
          number_of_ads: parseInt(item.number_of_ads) || 0,
          total_impressions: parseInt(item.total_impressions) || 0,
          total_clicks: parseInt(item.total_clicks) || 0,
          total_spend: parseFloat(item.total_spend) || 0,
          total_conversions: parseInt(item.total_conversions) || 0,
          total_revenue: parseFloat(item.total_revenue) || 0,
          ctr: parseFloat(item.ctr) || 0,
          cvr: parseFloat(item.cvr) || 0,
          roas: parseFloat(item.roas) || 0,
          cpm: parseFloat(item.cpm) || 0,
          cpa: parseFloat(item.cpa) || 0,
          active_days: parseInt(item.active_days) || 0
        }));
      }
      return [];
    } catch (error) {
      console.error(`Error getting group by analysis for ${groupByField}:`, error);
      return [];
    }
  }
};
